import React, { useState, useEffect } from 'react'
import SelectField from '../common/SelectField.js';
import { useNavigate } from 'react-router-dom'
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from '../common/Loader'
import Alert from '../common/Alert'
import vehicleContractService from '../../services/vehicleContractService'
import { ArrowLeft, ScrollText } from 'lucide-react'
import Button from '../common/Button.js'
import axios from '../../config/axios'
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js'
import { useTranslation } from 'react-i18next';


const AddVehicleContract = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)
  const [parties, setParties] = useState([])
  const [vehicles, setVehicles] = useState([])
  const [loadingData, setLoadingData] = useState(true)

  const [formData, setFormData] = useState({
    contract_party_id: '',
    vehicle_id: '',
    from_date: '',
    to_date: '',
    cost_per_km: '',
    contracted_km_limit: '',
    total_contracted_cost: '',
    per_day_running_km_limit: '',
    status: 'active'
  })

  const { errors, validate, validateField } = useValidation()

  const validationRules = {
    contract_party_id:      (v) => validators.select(v, 'contract party'),
    vehicle_id:             (v) => validators.select(v, 'vehicle'),
    from_date:              (v) => validators.required(v, 'From date'),
    to_date:                (v) => validators.required(v, 'To date'),
    cost_per_km:            (v) => validators.required(v, 'Cost per KM') || validators.number(v, { min: 0, label: 'Cost per KM' }),
    contracted_km_limit:    (v) => validators.required(v, 'Contracted KM limit') || validators.number(v, { min: 0, label: 'KM limit' }),
    total_contracted_cost:  (v) => validators.required(v, 'Total contracted cost') || validators.number(v, { min: 0, label: 'Total cost' }),
    per_day_running_km_limit: (v) => validators.required(v, 'Per day KM limit') || validators.number(v, { min: 0, label: 'Per day KM' }),
  }

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoadingData(true)
      const [partiesRes, vehiclesRes] = await Promise.all([
        axios.get('/contract-parties?all=true'),
        axios.get('/vehicles?all=true')
      ])

      setParties(partiesRes.data.data || [])
      setVehicles(vehiclesRes.data.data || [])
    } catch (err) {
      Alert.error('Failed to load parties or vehicles')
      console.error(err)
    } finally {
      setLoadingData(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target

    let filtered = value
    if (['cost_per_km', 'contracted_km_limit', 'total_contracted_cost', 'per_day_running_km_limit'].includes(name)) {
      filtered = inputFilters.amount(value)
    }

    setFormData(prev => ({ ...prev, [name]: filtered }))

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name])
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate(formData, validationRules)) {
      Alert.error('Please fix the highlighted errors before submitting')
      return
    }

    if (formData.from_date && formData.to_date && new Date(formData.from_date) > new Date(formData.to_date)) {
      Alert.error('To date must be after from date')
      return
    }

    try {
      setSubmitting(true)
      await vehicleContractService.create({
        ...formData,
        cost_per_km: parseFloat(formData.cost_per_km),
        contracted_km_limit: parseFloat(formData.contracted_km_limit),
        total_contracted_cost: parseFloat(formData.total_contracted_cost),
        per_day_running_km_limit: parseFloat(formData.per_day_running_km_limit),
        contract_party_id: parseInt(formData.contract_party_id),
        vehicle_id: parseInt(formData.vehicle_id)
      })

      Alert.success('Vehicle contract created successfully!')
      navigate('/vehicle-contracts')
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create contract')
      console.error(err)
    } finally {
      setSubmitting(false)
    }
  }

  if (loadingData) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading form data..." />
        </div>
      </DashboardLayout>
    )
  }

  const inputClass = (field) =>
    `w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${
      errors[field] ? 'border-red-400' : 'border-gray-300'
    }`

  return (
    <DashboardLayout>
      <div className="min-h-screen w-full max-w-[1440px] mx-auto px-4 py-6 sm:px-6 lg:px-10">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 sm:p-7 xl:p-10">
          <div className="flex flex-col gap-4 mb-8 border-b border-gray-200 pb-5 sm:flex-row sm:items-center sm:justify-between sm:pb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-xl">
                <ScrollText className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Add Vehicle Contract</h1>
                <p className="text-sm sm:text-base text-gray-600 mt-1">Create a new vehicle contract for your fleet.</p>
              </div>
            </div>

            <Button
              variant="ghost"
              onClick={() => navigate('/vehicle-contracts')}
              title={t('common.goBack')}
              className="self-start sm:self-auto"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
              <span className="ml-2 hidden sm:inline">Back to list</span>
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-gray-700 mb-5">Contract Details</h2>
              <div className="grid grid-cols-2 lg:grid-cols-2 gap-2 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contract Party <span className="text-red-500">*</span>
                  </label>
                  <SelectField variant="form"
                    name="contract_party_id"
                    value={formData.contract_party_id}
                    onChange={handleChange}
                    className={inputClass('contract_party_id')}
                  >
                    <option value="">Select contract party</option>
                    {parties.map(party => (
                      <option key={party.id} value={party.id}>
                        {party.name} ({party.company_name})
                      </option>
                    ))}
                  </SelectField>
                  <ValidationMessage error={errors.contract_party_id} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vehicle <span className="text-red-500">*</span>
                  </label>
                  <SelectField variant="form"
                    name="vehicle_id"
                    value={formData.vehicle_id}
                    onChange={handleChange}
                    className={inputClass('vehicle_id')}
                  >
                    <option value="">Select vehicle</option>
                    {vehicles.map(vehicle => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.vehicle_number} - {vehicle.vehicle_name}
                      </option>
                    ))}
                  </SelectField>
                  <ValidationMessage error={errors.vehicle_id} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="from_date"
                    value={formData.from_date}
                    onChange={handleChange}
                    className={inputClass('from_date')}
                  />
                  <ValidationMessage error={errors.from_date} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="to_date"
                    value={formData.to_date}
                    onChange={handleChange}
                    className={inputClass('to_date')}
                  />
                  <ValidationMessage error={errors.to_date} />
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-base sm:text-lg font-semibold text-gray-700 mb-5">Cost & Limits</h2>
              <div className="grid grid-cols-2 lg:grid-cols-2 gap-2 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cost per KM (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="cost_per_km"
                    value={formData.cost_per_km}
                    onChange={handleChange}
                    placeholder="0.00"
                    className={inputClass('cost_per_km')}
                  />
                  <ValidationMessage error={errors.cost_per_km} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contracted KM Limit <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="contracted_km_limit"
                    value={formData.contracted_km_limit}
                    onChange={handleChange}
                    placeholder="0.00"
                    className={inputClass('contracted_km_limit')}
                  />
                  <ValidationMessage error={errors.contracted_km_limit} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Per Day Running KM Limit <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="per_day_running_km_limit"
                    value={formData.per_day_running_km_limit}
                    onChange={handleChange}
                    placeholder="0.00"
                    className={inputClass('per_day_running_km_limit')}
                  />
                  <ValidationMessage error={errors.per_day_running_km_limit} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Contracted Cost (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="total_contracted_cost"
                    value={formData.total_contracted_cost}
                    onChange={handleChange}
                    placeholder="0.00"
                    className={inputClass('total_contracted_cost')}
                  />
                  <ValidationMessage error={errors.total_contracted_cost} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <SelectField variant="form"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base font-medium text-gray-700"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="completed">Completed</option>
                  </SelectField>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-end gap-3 pt-5 border-t border-gray-200">
              <Button
                variant="primary"
                type="submit"
                loading={submitting}
                disabled={submitting}
                className="w-full sm:w-auto"
              >
                {submitting ? 'Creating...' : 'Create Contract'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate('/vehicle-contracts')}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  )
}

export default AddVehicleContract
